import { Fragment, useMemo, useRef } from "react";
import { createAppColumnHelper, useClientTable } from "../../../../components/table/tableFeatures";
import { css } from "../../../../../styled-system/css";
import { Box, HStack, VStack } from "../../../../../styled-system/jsx";
import { formatCurrency, formatNumber, formatPercent } from "../../../../utils/formatNumber";
import { castNumbersInRowData, pathLabelMapping, parsePathToCampaignArray } from "../../utils/pathToPurchase/utils";
import { Badge } from "react-bootstrap";
import { FaArrowDown } from "react-icons/fa";
import { MdRotateLeft } from "react-icons/md";
import ProgressBar from "../../../../components/core/basic/ProgressBar";
import { Button } from "../../../../components/core/basic/Button";
import { type ConversionPathData, type ConversionPathDataWithAdTypes } from "../../utils/pathToPurchase/types";
import { RT8ColumnSelector } from "../../../../components/core/blocks/MetricSelector";
import { RT8FilterWidget } from "../../../merchandising/components/tables/FilterWidget";
import { Link } from "@tanstack/react-router";
import { useAMCReportData } from "../../hooks/useAMCReportData";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "../../../../zodios/client";
import { useAppSelector } from "../../../../redux/hooks";
import type { CampaignAdMapItem, CampaignAdMapResponse } from "../../../../zodios/apiDefinitions/campaign_ad_map";
import { Table } from "../../../../components/table/Table";
import { RT8Pagination } from "../../../../components/table/Pagination";
import { usePersistedTableState } from "../../../../components/table/usePersistedTableState";
import { resetTable } from "../../../../components/table/utils";
import { customFilterFn } from "../../../../components/table/filterFns";
import { SearchInput } from "../../../../components/fields/base/TextField";
import { useTranslation } from "react-i18next";

interface Campaign {
  id: number;
  name: string;
}

type CampaignNameField =
  "manual_campaign_name" | "auto_campaign_name" | "contextual_campaign_name" | "audience_campaign_name";

interface Props {
  row: ConversionPathData;
  campaignReportId?: number;
}

const normalizeCampaignAdMap = (response: CampaignAdMapResponse): CampaignAdMapItem[] => {
  return (response as { results: CampaignAdMapItem[] }).results;
};

const createCampaignLookup = (
  advertisements: CampaignAdMapItem[],
  key: CampaignNameField,
): { [key: string]: Campaign } => {
  return advertisements.reduce(
    (acc: { [x: string]: Campaign }, advertisement: CampaignAdMapItem) => {
      const campaignName = advertisement[key];
      if (!campaignName) {
        return acc;
      }
      acc[campaignName] = { name: advertisement.ad_name, id: advertisement.ad_id };
      return acc;
    },
    {} as { [key: string]: Campaign },
  );
};

const columnHelper = createAppColumnHelper<ConversionPathDataWithAdTypes>();

type CampaignLookups = {
  manual: Record<string, Campaign>;
  auto: Record<string, Campaign>;
  contextual: Record<string, Campaign>;
  audience: Record<string, Campaign>;
};

const PathToPurchaseExpander = ({ row, campaignReportId }: Props) => {
  const { t } = useTranslation(["amc"]);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);

  const { setters, state, initialState } = usePersistedTableState({
    persistKey: "CAMPAIGN_PATH_EXPANDER_TABLE",
    initialState: {
      sorting: [{ id: "impressions", desc: true }],
      pagination: { pageIndex: 0, pageSize: 10 },
      columnPinning: { start: ["path"], end: [] },
      columnVisibility: {
        users_that_purchased: false,
        user_purchase_rate: false,
        ntb_users_that_purchased: false,
        ntb_sales_amount: false,
        ntb_purchases: false,
        ntb_percentage: false,
      },
    },
  });

  const lookupsRef = useRef<CampaignLookups>({
    manual: {},
    auto: {},
    contextual: {},
    audience: {},
  });

  const { data: campaignData = [], isLoading } = useAMCReportData(campaignReportId);

  const path = useMemo(
    () =>
      row.path.match(/[A-Z]+-[A-Za-z]+/g)?.map((touchpoint: string) => {
        const key = touchpoint.split("-")[0] as keyof typeof pathLabelMapping;
        return t(pathLabelMapping[key].label);
      }),
    [row.path, t],
  );

  const tableData = useMemo(() => {
    const filteredCampaignData = campaignData
      .filter((row: ConversionPathData) => row.path.length)
      .map((row: ConversionPathData) => {
        if (row.sales_amount) {
          const acos = row.total_cost / row.sales_amount;
          row.acos = acos;
        }
        const adTypePath = parsePathToCampaignArray(row.path).map(({ type }) => {
          if (type.includes("SD")) {
            return t(($) => $.amc["Sponsored Display"]);
          }
          if (type.includes("SB")) {
            return t(($) => $.amc["Sponsored Brand"]);
          }
          if (type.includes("DSP")) {
            return t(($) => $.amc["DSP"]);
          }
          if (type.includes("SP")) {
            return t(($) => $.amc["Sponsored Product"]);
          }
          if (type.includes("ST")) {
            return t(($) => $.amc["Streaming TV"]);
          }

          return t(($) => $.amc["Unknown Ad Type"]);
        });

        return {
          adTypePath: adTypePath.filter((value: string, index: number) => {
            return index === 0 || value !== adTypePath[index - 1];
          }),
          ...castNumbersInRowData(row),
        };
      })
      .filter((row: ConversionPathDataWithAdTypes) => {
        if (!path || row.adTypePath.length !== path.length) {
          return false;
        }
        // Non-null assertion is safe — path.length is verified above
        return row.adTypePath.every((value: string, index: number) => value === path[index]!);
      });
    return filteredCampaignData;
  }, [path, campaignData, t]);

  const metricLimits = useMemo(() => {
    return tableData.reduce((acc: ConversionPathData, row: ConversionPathData) => {
      for (const key in row) {
        const value = row[key];

        if (typeof value === "number") {
          acc[key] = Math.max((acc[key] as number) || -Infinity, value);
        } else if (typeof value === "string") {
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            acc[key] = Math.max((acc[key] as number) || -Infinity, numValue);
          }
        }
      }
      return acc;
    }, {} as ConversionPathData);
  }, [tableData]);

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.path, {
        id: "path",
        header: t(($) => $.amc["Path"]),
        size: 450,
        minSize: 450,
        enableSorting: false,
        enableColumnFilter: false,
        cell: (info) => {
          const { path } = info.row.original;
          const { manual, auto, contextual, audience } = lookupsRef.current;

          const pathArray = parsePathToCampaignArray(path);

          return (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                fontWeight: "500",
                fontSize: "1.5rem",
                alignItems: "center",
                paddingLeft: "16px",
              }}
            >
              {pathArray.map((campaign, index) => {
                const { name, type } = campaign;
                const campaignTypeOpacity = pathLabelMapping[type as keyof typeof pathLabelMapping].opacity;

                let associatedMediaPlan;
                if (name && auto[name]) {
                  associatedMediaPlan = { ...auto[name], type: t(($) => $.amc["Auto"]) };
                }

                if (name && manual[name]) {
                  associatedMediaPlan = { ...manual[name], type: t(($) => $.amc["Discovery"]) };
                }

                if (name && contextual[name]) {
                  associatedMediaPlan = { ...contextual[name], type: t(($) => $.amc["Contextual"]) };
                }

                if (name && audience[name]) {
                  associatedMediaPlan = { ...audience[name], type: t(($) => $.amc["Audience"]) };
                }

                return (
                  <Fragment key={index}>
                    {associatedMediaPlan ?
                      <Link
                        to="/user/advertising/dashboard/ads/$id"
                        params={{ id: String(associatedMediaPlan.id) }}
                        target="_blank"
                      >
                        <Badge
                          className="badge bg-trellis-purple fs-6 mb-2 me-2 text-wrap"
                          style={{ opacity: campaignTypeOpacity, position: "relative", display: "block" }}
                        >
                          {associatedMediaPlan.name} ({associatedMediaPlan.type})
                          {index < pathArray.length - 1 ?
                            <div style={{ position: "absolute", left: 0, right: 0, width: "100%", bottom: "-12px" }}>
                              <FaArrowDown style={{ fontSize: "10px", color: "#333" }} />
                            </div>
                          : null}
                        </Badge>
                      </Link>
                    : <Badge
                        className="badge bg-trellis-purple fs-6 mb-2 me-2 text-wrap"
                        style={{ opacity: campaignTypeOpacity, position: "relative", display: "block" }}
                      >
                        {name}
                        {index < pathArray.length - 1 ?
                          <div style={{ position: "absolute", left: 0, right: 0, width: "100%", bottom: "-12px" }}>
                            <FaArrowDown style={{ fontSize: "10px", color: "#333" }} />
                          </div>
                        : null}
                      </Badge>
                    }
                  </Fragment>
                );
              })}
            </div>
          );
        },
      }),
      columnHelper.accessor((row) => row.path_occurrences, {
        id: "path_occurrences",
        header: t(($) => $.amc["Path Occurrences"]),
        size: 250,
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <HStack gap="small" alignItems="center">
              {formatNumber(v)}
              <ProgressBar progress={(v / metricLimits.path_occurrences) * 100} width={65} />
            </HStack>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      }),
      columnHelper.accessor((row) => row.impressions, {
        id: "impressions",
        header: t(($) => $.amc["Impressions"]),
        size: 230,
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <HStack gap="small" alignItems="center">
              {formatNumber(v)}
              <ProgressBar progress={(v / metricLimits.impressions) * 100} width={65} />
            </HStack>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      }),
      columnHelper.accessor((row) => row.total_cost, {
        id: "cost",
        header: t(($) => $.amc["Cost"]),
        size: 230,
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <HStack gap="small" alignItems="center">
              {formatCurrency(v, { marketPlace: "USA", decimalPlaces: 0 })}
              <ProgressBar progress={(v / metricLimits.total_cost) * 100} width={65} />
            </HStack>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "currency",
          },
        },
      }),
      columnHelper.accessor((row) => row.users_that_purchased, {
        id: "users_that_purchased",
        header: t(($) => $.amc["Users That Purchased"]),
        size: 270,
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <HStack gap="small" alignItems="center">
              {formatNumber(v)}
              <ProgressBar progress={(v / metricLimits.users_that_purchased) * 100} width={65} />
            </HStack>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      }),
      columnHelper.accessor((row) => row.sales_amount, {
        id: "sales_amount",
        header: t(($) => $.amc["Sales Amount"]),
        size: 230,
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <HStack gap="small" alignItems="center">
              {formatCurrency(v, { marketPlace: "USA", decimalPlaces: 0 })}
              <ProgressBar progress={(v / metricLimits.sales_amount) * 100} width={65} />
            </HStack>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "currency",
          },
        },
      }),
      columnHelper.accessor((row) => row.purchases, {
        id: "purchases",
        header: t(($) => $.amc["Purchases"]),
        size: 210,
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <HStack gap="small" alignItems="center">
              {formatNumber(v)}
              <ProgressBar progress={(v / metricLimits.purchases) * 100} width={65} />
            </HStack>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      }),
      columnHelper.accessor((row) => Number(row.acos ?? 0), {
        id: "acos",
        header: t(($) => $.amc["ACOS"]),
        size: 160,
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <HStack gap="small" alignItems="center">
              {formatPercent(v, { decimalPlaces: 0 })}
              <ProgressBar progress={v * 100} width={65} />
            </HStack>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "percent",
          },
        },
      }),
      columnHelper.accessor((row) => row.user_purchase_rate ?? 0, {
        id: "user_purchase_rate",
        header: t(($) => $.amc["User Purchase Rate"]),
        size: 250,
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <HStack gap="small" alignItems="center">
              {formatPercent(v, { decimalPlaces: 1 })}
              <ProgressBar progress={(v / (metricLimits.user_purchase_rate as number)) * 100} width={65} />
            </HStack>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "percent",
          },
        },
      }),
      columnHelper.accessor((row) => row.ntb_users_that_purchased, {
        id: "ntb_users_that_purchased",
        header: t(($) => $.amc["NTB Users That Purchased"]),
        size: 290,
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <HStack gap="small" alignItems="center">
              {v}
              <ProgressBar progress={(v / metricLimits.ntb_users_that_purchased) * 100} width={65} />
            </HStack>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      }),
      columnHelper.accessor((row) => row.ntb_sales_amount, {
        id: "ntb_sales_amount",
        header: t(($) => $.amc["NTB Sales Amount"]),
        size: 250,
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <HStack gap="small" alignItems="center">
              {formatCurrency(v, { marketPlace: "USA", decimalPlaces: 0 })}
              <ProgressBar progress={(v / metricLimits.ntb_sales_amount) * 100} width={65} />
            </HStack>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "currency",
          },
        },
      }),
      columnHelper.accessor((row) => row.ntb_purchases, {
        id: "ntb_purchases",
        header: t(($) => $.amc["NTB Purchases"]),
        size: 230,
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <HStack gap="small" alignItems="center">
              {formatNumber(v)}
              <ProgressBar progress={(v / metricLimits.ntb_purchases) * 100} width={65} />
            </HStack>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "number",
          },
        },
      }),
      columnHelper.accessor((row) => row.ntb_percentage ?? 0, {
        id: "ntb_percentage",
        header: t(($) => $.amc["NTB Percentage"]),
        size: 230,
        cell: ({ getValue }) => {
          const v = getValue();
          return (
            <HStack gap="small" alignItems="center">
              {formatPercent(v, { decimalPlaces: 1 })}
              <ProgressBar progress={(v / (metricLimits.ntb_percentage as number)) * 100} width={65} />
            </HStack>
          );
        },
        meta: {
          columnFilterOptions: {
            format: "percent",
          },
        },
      }),
    ],
    [metricLimits, t],
  );

  const table = useClientTable({
    data: tableData,
    columns,
    defaultColumn: {
      filterFn: customFilterFn,
    },
    globalFilterFn: (row, _columnId, filterValue) => {
      if (!filterValue) return true;
      return row.original.path.toLowerCase().includes(String(filterValue).toLowerCase());
    },
    meta: {
      showSkeleton: isLoading,
    },
    initialState,
    state,
    ...setters,
  });

  const visibleRows = table.getRowModel().rows;

  const visibleCampaignNames = useMemo(() => {
    const names = new Set<string>();
    for (const row of visibleRows) {
      const parsed = parsePathToCampaignArray(row.original.path);
      for (const entry of parsed) {
        if (entry.name) names.add(entry.name);
      }
    }
    return [...names];
  }, [visibleRows]);

  const { data: campaignAdMap = [] } = useQuery<CampaignAdMapResponse, unknown, CampaignAdMapItem[]>({
    queryKey: ["campaign_ad_map", marketPlace, visibleCampaignNames],
    queryFn: async () =>
      await apiClient.listCampaignAdMap({
        queries: {
          marketplace: marketPlace,
          campaign_names: visibleCampaignNames.join(","),
          page_size: visibleCampaignNames.length,
        },
      }),
    select: normalizeCampaignAdMap,
    enabled: marketPlace !== "None" && visibleCampaignNames.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  lookupsRef.current = {
    manual: createCampaignLookup(campaignAdMap, "manual_campaign_name"),
    auto: createCampaignLookup(campaignAdMap, "auto_campaign_name"),
    contextual: createCampaignLookup(campaignAdMap, "contextual_campaign_name"),
    audience: createCampaignLookup(campaignAdMap, "audience_campaign_name"),
  };

  return (
    <Box>
      <p className={css({ fontSize: "small" })}>
        {t(($) => $.amc["* Not all campaign paths can be mapped due to insufficient unique users"])}
      </p>
      <HStack padding="small" fontSize="large" justifyContent="space-between" alignItems="start">
        <VStack flex="1" alignItems="start">
          <HStack width="100%">
            <Button
              variant="contentOnly"
              onPress={() => {
                resetTable(table);
              }}
              fontSize="xxlarge"
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.amc["Search paths"])}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={state.globalFilter ?? ""}
              onChange={(value) => setters.onGlobalFilterChange(value)}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination table={table} />
      </HStack>
      <Table
        table={table}
        className={css({
          minWidth: "100%",
          "& th": {
            paddingY: "small",
          },
          "& td": {
            paddingY: "medium",
          },
        })}
      />
    </Box>
  );
};

export default PathToPurchaseExpander;
